import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, RefreshCw, Search, Smartphone, StickyNote } from 'lucide-react';
import clsx from 'clsx';

import PageHeader from '@/components/PageHeader';
import {
  listPeopleUsers,
  type PeopleUserListItem,
  type PeopleUsersQuery,
} from '@/lib/operatorApi';
import { formatDate, formatPhone, relativeTime } from '@/lib/format';

// Пользователи гостевого приложения People. Гость — User с role=CLIENT;
// «источник» отличает аккаунты приложения (вход без привязки к заведению или
// с push-подпиской People) от гостей web-меню конкретного заведения.

const PAGE = 50;

type StatusFilter = NonNullable<PeopleUsersQuery['status']>;
type SourceFilter = NonNullable<PeopleUsersQuery['source']>;
type Sort = NonNullable<PeopleUsersQuery['sort']>;

export default function AppUsers() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('people');
  const [sort, setSort] = useState<Sort>('created');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<PeopleUserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listPeopleUsers({ q: q.trim() || undefined, status, source, sort, limit: PAGE, offset })
      .then((r) => {
        setItems(r.results);
        setTotal(r.total);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q, status, source, sort, offset]);

  // Поиск — с задержкой, чтобы не дёргать бэкенд на каждую букву.
  useEffect(() => {
    const t = window.setTimeout(load, 250);
    return () => window.clearTimeout(t);
  }, [load]);

  const pageFrom = total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + PAGE, total);

  return (
    <div>
      <PageHeader
        eyebrow="Гостевое приложение"
        title="Пользователи"
        description="Гости приложения People: профиль, заказы и брони, блокировка. Заблокированный гость не сможет войти и делать заказы."
        actions={
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            <RefreshCw size={13} strokeWidth={1.75} />
            Обновить
          </button>
        }
      />

      <div className="page-pad">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          <label className="relative flex-1">
            <Search
              size={14}
              strokeWidth={1.75}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"
            />
            <input
              className="input !pl-9"
              placeholder="Имя, телефон, email или ID"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
            />
          </label>

          <div className="flex flex-wrap gap-3">
            <Chips<SourceFilter>
              value={source}
              options={[
                ['people', 'People'],
                ['menu', 'Web-меню'],
                ['all', 'Все'],
              ]}
              onChange={(v) => {
                setSource(v);
                setOffset(0);
              }}
            />
            <Chips<StatusFilter>
              value={status}
              options={[
                ['all', 'Все'],
                ['active', 'Активные'],
                ['blocked', 'Заблокированные'],
                ['deleted', 'Удалённые'],
              ]}
              onChange={(v) => {
                setStatus(v);
                setOffset(0);
              }}
            />
            <select
              className="input !w-auto"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as Sort);
                setOffset(0);
              }}
              aria-label="Сортировка"
            >
              <option value="created">Сначала новые</option>
              <option value="lastLogin">По последнему входу</option>
              <option value="orders">По числу заказов</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-2 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <div className="card table-scroll">
          <table className="table-grid">
            <thead>
              <tr>
                <th>Гость</th>
                <th>Контакты</th>
                <th>Источник</th>
                <th className="text-right">Заказы</th>
                <th className="text-right">Брони</th>
                <th className="text-right">Отзывы</th>
                <th>Последний вход</th>
                <th>Регистрация</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr
                  key={u.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/app/users/${u.id}`)}
                >
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.name || 'Без имени'}</span>
                      {u.hasPeopleApp && (
                        <Smartphone
                          size={13}
                          strokeWidth={1.75}
                          className="text-ink/40"
                          aria-label="Установлено приложение"
                        />
                      )}
                      {u.hasNote && (
                        <StickyNote
                          size={13}
                          strokeWidth={1.75}
                          className="text-amber-500"
                          aria-label="Есть заметка оператора"
                        />
                      )}
                    </div>
                    <div className="font-mono text-[11px] text-ink/40">{u.id}</div>
                  </td>
                  <td className="text-ink/80">
                    <div>{formatPhone(u.phone)}</div>
                    <div className="text-[12px] text-ink/60">{u.email ?? '—'}</div>
                  </td>
                  <td>
                    {u.source === 'people' ? (
                      <span className="badge-orange">People</span>
                    ) : (
                      <span className="badge-ink">Web-меню</span>
                    )}
                    {u.establishment && (
                      <div className="mt-1 text-[12px] text-ink/60">
                        <Link
                          to={`/tenants/${u.establishment.id}`}
                          className="link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {u.establishment.name}
                        </Link>
                      </div>
                    )}
                  </td>
                  <td className="text-right font-mono text-[12px]">{u.counts.orders}</td>
                  <td className="text-right font-mono text-[12px]">{u.counts.reservations}</td>
                  <td className="text-right font-mono text-[12px]">{u.counts.reviews}</td>
                  <td className="text-ink/70" title={u.lastLoginAt ?? undefined}>
                    {u.lastLoginAt ? relativeTime(u.lastLoginAt) : '—'}
                  </td>
                  <td className="text-ink/70">{formatDate(u.createdAt)}</td>
                  <td>
                    <UserStatusBadge status={u.status} />
                  </td>
                  <td className="text-right text-ink/40">
                    <ChevronRight size={14} strokeWidth={1.75} />
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-ink/50">
                    Гостей не найдено.
                  </td>
                </tr>
              )}
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-ink/40">
                    Загрузка…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {total > PAGE && (
          <div className="mt-4 flex items-center justify-between text-[13px] text-ink/60">
            <span>
              {pageFrom}–{pageTo} из {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
                disabled={offset === 0 || loading}
              >
                Назад
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={() => setOffset((o) => o + PAGE)}
                disabled={offset + PAGE >= total || loading}
              >
                Дальше
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Chips<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-1">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          className={clsx('btn !px-3 !py-1.5 text-[12px]', value === v ? 'bg-ink text-white' : 'btn-outline')}
          onClick={() => onChange(v)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function UserStatusBadge({ status }: { status: PeopleUserListItem['status'] }) {
  if (status === 'ACTIVE') return <span className="badge-green">Активен</span>;
  if (status === 'BLOCKED') return <span className="badge-red">Заблокирован</span>;
  return <span className="badge-ink">Удалён</span>;
}
